import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { taskService } from '../services/taskService';
import { statusService } from '../services/statusService';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import InlineToast from '../components/common/InlineToast';
import TableSkeleton from '../components/common/TableSkeleton';

const MAX_ACTIVE_TASKS = 5;

const getDefaultFormValues = (task) => ({
  date: new Date().toISOString().split('T')[0],
  timeSpentHours: '',
  statusText: '',
  externalLink: ''
});

const StatusPage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [perTaskFormState, setPerTaskFormState] = useState({});
  const [globalMessage, setGlobalMessage] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'info' });
  const [taskSearch, setTaskSearch] = useState('');
  const textRefs = useRef({});

  const loadTasks = useCallback(async () => {
    try {
      if (!user?.id) return;
      setTasksLoading(true);
      const response = await taskService.getTasks({
        assignedTo: user.id,
        assignmentState: 'active',
        limit: MAX_ACTIVE_TASKS
      });
      const assignedActiveTasks = (response.data || [])
        .filter((task) => task.assignee?.id === user.id || (task.subAssignees || []).some((member) => member.id === user.id))
        .sort((a, b) => {
          const aDue = a.revisedDate || a.targetDate || '9999-12-31';
          const bDue = b.revisedDate || b.targetDate || '9999-12-31';
          return aDue.localeCompare(bDue);
        })
        .slice(0, MAX_ACTIVE_TASKS);
      setTasks(assignedActiveTasks);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      setGlobalMessage('Failed to load assigned tasks.');
      setToast({ message: 'Failed to load assigned tasks.', type: 'error' });
    } finally {
      setTasksLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const updateFormState = (taskId, patch) => {
    setPerTaskFormState(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        ...patch,
        values: {
          ...(prev[taskId]?.values || {}),
          ...(patch.values || {})
        }
      }
    }));
  };

  const handleAddTask = (task) => {
    if (selectedTaskIds.includes(task.id)) return;
    setSelectedTaskIds(prev => [...prev, task.id]);
    setPerTaskFormState(prev => ({
      ...prev,
      [task.id]: {
        values: getDefaultFormValues(task),
        error: '',
        success: '',
        submitting: false,
        submitted: false,
        statusUpdateId: null,
        isEditing: false
      }
    }));
  };

  const handleRemoveTask = (taskId) => {
    setSelectedTaskIds(prev => prev.filter(id => id !== taskId));
    setPerTaskFormState(prev => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    delete textRefs.current[taskId];
  };

  const handleFieldChange = (taskId, field, value) => {
    updateFormState(taskId, {
      values: { [field]: value },
      error: '',
      success: ''
    });
  };

  const applyTextChange = (taskId, nextText, selectionStart, selectionEnd) => {
    handleFieldChange(taskId, 'statusText', nextText);
    requestAnimationFrame(() => {
      const ref = textRefs.current[taskId];
      if (ref) {
        ref.focus();
        ref.setSelectionRange(selectionStart, selectionEnd);
      }
    });
  };

  const handleBold = (taskId) => {
    const ref = textRefs.current[taskId];
    const form = perTaskFormState[taskId];
    if (!ref || !form) return;

    const { selectionStart, selectionEnd } = ref;
    const value = form.values.statusText || '';

    if (selectionStart === selectionEnd) {
      const next = `${value.slice(0, selectionStart)}****${value.slice(selectionEnd)}`;
      applyTextChange(taskId, next, selectionStart + 2, selectionStart + 2);
      return;
    }

    const selected = value.slice(selectionStart, selectionEnd);
    const wrapped = `**${selected}**`;
    const next = `${value.slice(0, selectionStart)}${wrapped}${value.slice(selectionEnd)}`;
    applyTextChange(taskId, next, selectionStart + 2, selectionEnd + 2);
  };

  const handleBullets = (taskId) => {
    const ref = textRefs.current[taskId];
    const form = perTaskFormState[taskId];
    if (!ref || !form) return;

    const { selectionStart, selectionEnd } = ref;
    const value = form.values.statusText || '';

    if (selectionStart === selectionEnd) {
      const next = `${value.slice(0, selectionStart)}- ${value.slice(selectionEnd)}`;
      applyTextChange(taskId, next, selectionStart + 2, selectionStart + 2);
      return;
    }

    const selected = value.slice(selectionStart, selectionEnd);
    const updated = selected
      .split('\n')
      .map(line => {
        if (line.trim() === '') return line;
        if (/^\s*[-*]\s+/.test(line)) return line;
        return `- ${line}`;
      })
      .join('\n');

    const next = `${value.slice(0, selectionStart)}${updated}${value.slice(selectionEnd)}`;
    applyTextChange(taskId, next, selectionStart, selectionStart + updated.length);
  };

  const validateForm = (values) => {
    if (!values.date) return 'Date is required.';
    if (!values.timeSpentHours) return 'Hours spent is required.';
    if (!values.statusText) return 'Status update text is required.';
    return '';
  };

  const handleSubmitTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const form = perTaskFormState[taskId];
    if (!task || !form) return;

    if (form.statusUpdateId && !form.isEditing) {
      updateFormState(taskId, {
        isEditing: true,
        success: '',
        error: ''
      });
      return;
    }

    const error = validateForm(form.values);
    if (error) {
      updateFormState(taskId, { error, success: '' });
      return;
    }

    updateFormState(taskId, { submitting: true, error: '', success: '' });

    try {
      const statusText = form.values.externalLink
        ? `${form.values.statusText}\n\nExternal Link: ${form.values.externalLink}`
        : form.values.statusText;

      if (form.statusUpdateId) {
        await statusService.updateStatusUpdate(form.statusUpdateId, {
          date: form.values.date,
          statusText,
          timeSpentHours: parseFloat(form.values.timeSpentHours)
        });

        updateFormState(taskId, {
          submitting: false,
          submitted: true,
          isEditing: false,
          success: 'Status updated successfully.',
          error: ''
        });
        setToast({ message: 'Status updated successfully.', type: 'success' });
      } else {
        const createResponse = await statusService.createStatusUpdate({
          taskId,
          date: form.values.date,
          statusText,
          timeSpentHours: parseFloat(form.values.timeSpentHours)
        });

        updateFormState(taskId, {
          submitting: false,
          submitted: true,
          statusUpdateId: createResponse?.data?.id || null,
          isEditing: false,
          success: 'Status submitted successfully. You can edit it for 3 days.',
          error: ''
        });
        setToast({ message: 'Status submitted successfully.', type: 'success' });
      }

      await loadTasks();
    } catch (err) {
      updateFormState(taskId, {
        submitting: false,
        success: '',
        error: err.response?.data?.message || 'Failed to submit status.'
      });
      setToast({ message: err.response?.data?.message || 'Failed to submit status.', type: 'error' });
    }
  };

  const visibleTasks = tasks.filter((task) => {
    if (!taskSearch) return true;
    const term = taskSearch.toLowerCase();
    return task.title.toLowerCase().includes(term) || (task.client?.name || '').toLowerCase().includes(term);
  });

  if (tasksLoading) {
    return (
      <div>
        <Loading />
        <div className="mt-4">
          <TableSkeleton rows={8} columns={9} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Submit Status</h1>
        <p className="text-gray-600">Select your active tasks and submit updates individually (max 5 active tasks shown)</p>
      </div>

      {globalMessage && (
        <div className="p-4 rounded mb-4 bg-red-100 text-red-700">
          {globalMessage}
        </div>
      )}

      <div className="mb-4">
        <InlineToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'info' })}
        />
      </div>

      <div className="sticky top-16 z-20 bg-gray-50 pb-3">
        <div className="bg-white p-4 rounded-lg shadow-md mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Filter Tasks</label>
          <input
            type="text"
            value={taskSearch}
            onChange={(e) => setTaskSearch(e.target.value)}
            placeholder="Search by title or client..."
            className="w-full md:w-96 border rounded px-3 py-2"
          />
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No active tasks</h3>
          <p className="text-gray-600">You currently have no active assigned tasks to submit status for.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto mb-6">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assignee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target / Revised</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {visibleTasks.map(task => {
                const isSelected = selectedTaskIds.includes(task.id);
                return (
                  <tr key={task.id} className={isSelected ? 'bg-blue-50' : ''}>
                    <td className="px-4 py-3 text-primary align-top">
                      <div className="min-w-[14rem] max-w-[22rem] whitespace-normal break-words leading-snug">
                        {task.title}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="max-w-[9rem] truncate">{task.client?.name || '-'}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="max-w-[9rem] truncate">{task.category?.name || '-'}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {task.assignee?.name ? (
                        <div className="max-w-[8rem] leading-tight">
                          <div className="truncate">{task.assignee.name.split(' ')[0] || '-'}</div>
                          <div className="truncate">{task.assignee.name.split(' ').slice(1).join(' ') || ''}</div>
                        </div>
                      ) : (
                        <div className="max-w-[8rem] truncate">-</div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">{task.hierarchyLevel}</td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">{task.priority || '-'}</td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">{task.status || 'Not Started'}</td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <div className="leading-tight">
                        <div>{task.targetDate || '-'}</div>
                        {task.revisedDate && <div className="text-xs text-indigo-700">Revised: {task.revisedDate}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      {isSelected ? (
                        <Button size="sm" variant="secondary" onClick={() => handleRemoveTask(task.id)}>
                          Remove
                        </Button>
                      ) : (
                        <Button size="sm" variant="secondary" onClick={() => handleAddTask(task)}>
                          Add
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="space-y-4">
        {selectedTaskIds.length === 0 && tasks.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md text-gray-600">
            No tasks selected. Click Add on a task to create its status form.
          </div>
        )}

        {selectedTaskIds.map(taskId => {
          const task = tasks.find(t => t.id === taskId);
          const state = perTaskFormState[taskId];
          if (!task || !state) return null;
          const isReadOnly = state.statusUpdateId && !state.isEditing;

          return (
            <div key={taskId} className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">{task.title}</h2>
                {state.submitted && (
                  <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">Submitted</span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-xs uppercase text-gray-500">Title</p>
                  <p className="text-sm text-gray-800">{task.title}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Client</p>
                  <p className="text-sm text-gray-800">{task.client?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Category</p>
                  <p className="text-sm text-gray-800">{task.category?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Assignee</p>
                  <p className="text-sm text-gray-800">{task.assignee?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Level</p>
                  <p className="text-sm text-gray-800">{task.hierarchyLevel}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Priority</p>
                  <p className="text-sm text-gray-800">{task.priority || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Task Status</p>
                  <p className="text-sm text-gray-800">{task.status || 'Not Started'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Target Date</p>
                  <p className="text-sm text-gray-800">{task.targetDate || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Revised Date</p>
                  <p className="text-sm text-gray-800">{task.revisedDate || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Parent Task</p>
                  <p className="text-sm text-gray-800">{task.parentTask?.title || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Sub-Assignees</p>
                  <p className="text-sm text-gray-800">
                    {task.subAssignees?.length
                      ? task.subAssignees.map(member => member.name).join(', ')
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">External Link</p>
                  {task.externalLinkUrl ? (
                    <a
                      href={task.externalLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline break-all"
                    >
                      {task.externalLinkType ? `${task.externalLinkType.toUpperCase()} link` : 'Open link'}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-800">-</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={state.values.date}
                    onChange={(e) => handleFieldChange(taskId, 'date', e.target.value)}
                    disabled={isReadOnly}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours Spent</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="16"
                    value={state.values.timeSpentHours}
                    onChange={(e) => handleFieldChange(taskId, 'timeSpentHours', e.target.value)}
                    disabled={isReadOnly}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Status Update</label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => handleBullets(taskId)}
                    disabled={isReadOnly}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                  >
                    Bullet list
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBold(taskId)}
                    disabled={isReadOnly}
                    className="px-3 py-1 text-sm border rounded font-semibold hover:bg-gray-50"
                  >
                    Bold
                  </button>
                </div>
                <textarea
                  ref={(el) => { textRefs.current[taskId] = el; }}
                  value={state.values.statusText}
                  onChange={(e) => handleFieldChange(taskId, 'statusText', e.target.value)}
                  rows="4"
                  disabled={isReadOnly}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Describe work done, blockers, and outcomes..."
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">External Link (Optional)</label>
                <input
                  type="url"
                  value={state.values.externalLink}
                  onChange={(e) => handleFieldChange(taskId, 'externalLink', e.target.value)}
                  disabled={isReadOnly}
                  className="w-full border rounded px-3 py-2"
                  placeholder="https://example.com/ticket/123"
                />
              </div>

              {state.error && <p className="mt-3 text-sm text-red-600">{state.error}</p>}
              {state.success && <p className="mt-3 text-sm text-green-700">{state.success}</p>}

              <div className="mt-4 flex justify-end">
                <Button
                  type="button"
                  onClick={() => handleSubmitTask(taskId)}
                  disabled={state.submitting}
                >
                  {state.submitting ? 'Submitting...' : (state.statusUpdateId ? (state.isEditing ? 'Submit' : 'Edit') : 'Submit')}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StatusPage;
